import { EditableValue, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { JCodeDetails } from "../../../data-model/medicalPaOrdersModel";
import ChevronDown from "../../../svg/chevron-down";
import "../authorisationDetails.scss";
import {
  FEEDBACK_MODE_BADGE_LABEL,
  INVALID_BADGE_LABEL,
  PROCEDURE_FIELD_CONFIGS,
  REQUIRED_PROCEDURE_FIELDS,
  VALUE_CHANGED_BG,
  VALUE_CHANGED_LABEL,
  VALUE_CHANGED_TEXT,
  type ApprovalStatus,
  type ProcedureFieldState,
} from "../utils/constants";
import { getOriginalFieldState } from "../utils/saveReviewHelpers";
import RequiredAsterisk from "./RequiredAsterisk";
import SectionApprovalActions from "./SectionApprovalActions";

export interface ProcedureAccordionProps {
  jCode: JCodeDetails;
  isExpanded: boolean;
  onToggle: () => void;
  approvalStatus: ApprovalStatus;
  onApprovalStatusChange: (status: ApprovalStatus) => void;
  onProcedureChange?: (id: string, hasChange: boolean) => void;
  onFieldStateUpdate?: (id: string, state: ProcedureFieldState | null) => void;
  agentDiffChangedKeys?: Set<keyof ProcedureFieldState>;
  resetTrigger?: number;
  /**
   * When true, reject was loaded from persisted `human_validated` — no feedback-mode UI
   * until the user changes approval for this row.
   */
  serverRejectHydrated?: boolean;
  /**
   * Required fields currently holding invalid values. Non-empty → show a red
   * asterisk in the row header (this row is blocking Save).
   */
  requiredInvalidFieldKeys?: Set<keyof ProcedureFieldState>;
}

const ProcedureAccordion = ({
  jCode,
  isExpanded,
  onToggle,
  approvalStatus,
  onApprovalStatusChange,
  onProcedureChange,
  onFieldStateUpdate,
  agentDiffChangedKeys,
  resetTrigger = 0,
  serverRejectHydrated = false,
  requiredInvalidFieldKeys,
}: ProcedureAccordionProps) => {
  const id = jCode.id ?? jCode.j_code ?? "";
  const originalState = getOriginalFieldState(jCode);

  const [fieldState, setFieldState] = useState<ProcedureFieldState>(
    () => originalState,
  );

  useEffect(() => {
    if (resetTrigger === 0) return;
    setFieldState(getOriginalFieldState(jCode));
  }, [resetTrigger, jCode]);

  const setField = useCallback(
    (key: keyof ProcedureFieldState, value: string) => {
      setFieldState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSave = useCallback((_value: string) => {}, []);

  const changedKeys = (
    PROCEDURE_FIELD_CONFIGS as { key: keyof ProcedureFieldState }[]
  )
    .filter(({ key }) => fieldState[key] !== originalState[key])
    .map(({ key }) => key);
  const hasAnyChange = changedKeys.length > 0;
  const hasAgentDiffHighlight = (agentDiffChangedKeys?.size ?? 0) > 0;
  const showValueChangedOnRow = hasAnyChange || hasAgentDiffHighlight;

  useEffect(() => {
    onProcedureChange?.(id, hasAnyChange);
  }, [id, hasAnyChange, onProcedureChange]);

  useEffect(() => {
    onFieldStateUpdate?.(id, fieldState);
    return () => {
      onFieldStateUpdate?.(id, null);
    };
  }, [id, fieldState, onFieldStateUpdate]);

  const code = fieldState.cptCode;

  return (
    <div className="mb-2 overflow-hidden rounded border-b border-primaryGray-15 last:mb-0">
      <div
        role="button"
        tabIndex={0}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-3 text-left"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isExpanded}
      >
        <ChevronDown
          stroke="#0F0F0F"
          height={14}
          width={14}
          rotate={isExpanded ? 180 : 0}
        />
        <Tags label={code} backgroundColor="#F5F5F5" textColor="#0F0F0F" />
        {(requiredInvalidFieldKeys?.size ?? 0) > 0 && <RequiredAsterisk />}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          <span className="text-[0.6875rem] leading-[1.125rem] text-primaryGray-1">
            {jCode.medicine_name ?? jCode.description}
          </span>
          {!showValueChangedOnRow && approvalStatus && (
            <SectionApprovalActions
              approvalStatus={approvalStatus}
              onApprovalStatusChange={onApprovalStatusChange}
              variant="badge"
              invalidBadgeLabel={
                approvalStatus === "invalid" && serverRejectHydrated
                  ? INVALID_BADGE_LABEL
                  : FEEDBACK_MODE_BADGE_LABEL
              }
            />
          )}
          {showValueChangedOnRow && (
            <Tags
              label={VALUE_CHANGED_LABEL}
              backgroundColor={VALUE_CHANGED_BG}
              textColor={VALUE_CHANGED_TEXT}
            />
          )}
        </div>

        <div
          className="flex shrink-0 items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <SectionApprovalActions
            approvalStatus={approvalStatus}
            onApprovalStatusChange={onApprovalStatusChange}
            size="small"
            variant="chips"
          />
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-primaryGray-15 bg-white px-4 py-3">
          <div className="authorisation-details__row authorisation-details__row--three-cols">
            {PROCEDURE_FIELD_CONFIGS.map(
              ({ key, label, placeholder, fullWidth }) => {
                const value = fieldState[key];
                const isNonEditableField =
                  key === "cptCode" ||
                  key === "drugName" ||
                  key === "genericName";
                const isFieldChanged =
                  changedKeys.includes(key) ||
                  Boolean(agentDiffChangedKeys?.has(key));
                const fieldClassName = fullWidth
                  ? "authorisation-details__field authorisation-details__field--full"
                  : "authorisation-details__field";
                return (
                  <div key={key} className={fieldClassName}>
                    <div className="authorisation-details__label flex flex-wrap items-center gap-1.5">
                      {label}
                      {REQUIRED_PROCEDURE_FIELDS.includes(key) && (
                        <RequiredAsterisk />
                      )}
                      {isFieldChanged && (
                        <Tags
                          label={VALUE_CHANGED_LABEL}
                          backgroundColor={VALUE_CHANGED_BG}
                          textColor={VALUE_CHANGED_TEXT}
                        />
                      )}
                    </div>

                    <EditableValue
                      value={value}
                      onSave={(v) => {
                        setField(key, v);
                        handleSave(v);
                      }}
                      placeholder={placeholder}
                      showEditButton={
                        approvalStatus === "invalid" &&
                        !isNonEditableField &&
                        !serverRejectHydrated
                      }
                      className="authorisation-details__value text-[0.6875rem] leading-[1.125rem]"
                    />
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcedureAccordion;
